// components/DashboardLayout.tsx - Fixed navigation
import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
  Badge,
  Container,
  Chip,
  Stack,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  PersonAdd as PersonAddIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  Assessment as ReportIcon,
  Logout as LogoutIcon,
  Notifications as NotificationsIcon,
  Settings as SettingsIcon,
  ArrowDropDown as ArrowDropDownIcon,
  VerifiedUser as VerifiedIcon,
  Pending as PendingIcon,
  Timeline as TimelineIcon,
  BarChart as ChartIcon,
  Accessibility as AccessibilityIcon,
  Star, Email, 
  KeyboardArrowDown as KeyboardArrowDownIcon, 
  VpnKey as VpnKeyIcon,
  Copyright as CopyrightIcon,
  Favorite as FavoriteIcon,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { useAuth } from '../contexts/AuthContext';

const drawerWidth = 280;

const DashboardLayout: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, hasRole } = useAuth();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  // Data Entry Officer Menu Items
  const dataEntryMenuItems = [
    {
      text: 'Dashboard',
      icon: <DashboardIcon />,
      path: '/dashboard',
    },
    {
      text: 'Entry Form',
      icon: <PersonAddIcon />,
      path: '/data-entry',
    },
    {
      text: 'Edit / Delete',
      icon: <EditIcon />,
      path: '/manage-records',
    },
    {
      text: 'Reports',
      icon: <ReportIcon />,
      path: '/reports',
      subItems: [
        { text: 'Daily Summary (Own)', path: '/reports/daily-summary' },
        { text: 'Register Report', path: '/reports/register-report' },
      ],
    },
  ];

  // Data Manager Menu Items
  const dataManagerMenuItems = [
    {
      text: 'Dashboard',
      icon: <DashboardIcon />,
      path: '/dashboard',
    },
    {
      text: 'Entry Form',
      icon: <PersonAddIcon />,
      path: '/data-entry',
    },
    {
      text: 'Manage Records',
      icon: <EditIcon />,
      path: '/manage-records',
    },
    {
      text: 'Reports',
      icon: <ReportIcon />,
      path: '/reports',
      subItems: [
        { text: 'Daily Summary', path: '/reports/daily-summary' },
        { text: 'Register Report', path: '/reports/register-report' },
        { text: 'Audit Log', path: '/reports/audit-log' },
      ],
    },
    {
      text: 'Manage Staffs',
      icon: <AccessibilityIcon />,
      path: '/manage-staffs',
    },
  ];

  // Lead Manager Menu Items
  const leadManagerMenuItems = [
    {
      text: 'Dashboard',
      icon: <DashboardIcon />,
      path: '/dashboard',
    },
    {
      text: 'Manage Records',
      icon: <EditIcon />,
      path: '/manage-records',
    },
    {
      text: 'Reports',
      icon: <ReportIcon />,
      path: '/reports',
      subItems: [
        { text: 'Daily Summary', path: '/reports/daily-summary' },
        { text: 'Register Report', path: '/reports/register-report' },
        { text: 'Audit Log', path: '/reports/audit-log' },
      ],
    },
  ];

  const getMenuItems = () => {
    if (hasRole('DATA_ENTRY_OFFICER')) return dataEntryMenuItems;
    if (hasRole('DATA_MANAGER')) return dataManagerMenuItems;
    if (hasRole('LEAD_MANAGER')) return leadManagerMenuItems;
    return [];
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* User Profile Section */}
      <Box 
        sx={{ 
          p: 3, 
          borderBottom: `1px solid ${theme.palette.divider}`,
          background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.light, 0.05)} 100%)`,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
          <Avatar
            sx={{
              width: 56,
              height: 56,
              bgcolor: theme.palette.primary.main,
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              fontWeight: 'bold',
              fontSize: '1.5rem',
              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
              border: `3px solid ${theme.palette.background.paper}`,
              flexShrink: 0,
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'scale(1.05)',
                boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`,
              }
            }}
          >
            {user?.users_name ? user.users_name.trim().split(' ').pop()?.charAt(0).toUpperCase() : 'U'}
          </Avatar>
          
          <Box sx={{ ml: 2, flex: 1, minWidth: 0 }}> {/* minWidth: 0 for text truncation */}
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 700,
                background: `linear-gradient(90deg, ${theme.palette.text.primary}, ${theme.palette.primary.main})`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                mb: 0.5,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '100%',
              }}
              title={user?.users_name || 'User'}
            >
              {user?.users_name ? user.users_name.trim().split(' ').pop() : 'User'}
            </Typography>
              
            {/* Designation Chip with better text handling */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Chip
                icon={<Star fontSize="small" sx={{ color: 'inherit', fontSize: '12px' }} />}
                label={user?.designation || 'Designation'}
                size="small"
                sx={{
                  maxWidth: '100%',
                  height: '24px',
                  px: 1,
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.dark,
                  fontWeight: 600,
                  fontSize: '0.7rem',
                  borderRadius: '12px',
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  '& .MuiChip-label': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '150px', // Adjust based on your sidebar width
                  },
                  '& .MuiChip-icon': {
                    ml: 0.25,
                    mr: 0.5
                  }
                }}
                title={user?.designation || 'Designation'}
              />
            </Box>
          </Box>
        </Box>
        
        {/* Email Section with better text handling */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'flex-start', 
          mt: 2.5,
          p: 1.5,
          borderRadius: 2,
          bgcolor: alpha(theme.palette.background.default, 0.5),
          border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
          transition: 'all 0.2s ease',
          '&:hover': {
            bgcolor: alpha(theme.palette.background.default, 0.7),
            borderColor: alpha(theme.palette.primary.light, 0.3),
          }
        }}>
          <Email 
            fontSize="small" 
            sx={{ 
              color: theme.palette.primary.main, 
              mr: 1.5,
              mt: 0.25,
              opacity: 0.8,
              flexShrink: 0
            }} 
          />
          <Typography 
            variant="body2" 
            sx={{ 
              color: theme.palette.text.secondary,
              fontWeight: 500,
              letterSpacing: '0.02em',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '100%',
              flex: 1,
            }}
            title={user?.email || 'email@example.com'}
          >
            {user?.email || 'email@example.com'}
          </Typography>
        </Box>
      </Box>

      {/* Navigation Menu */}
      <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
        <List>
          {getMenuItems().map((item, index) => (
            <React.Fragment key={index}>
              <ListItem disablePadding>
                <ListItemButton
                  selected={location.pathname === item.path}
                  onClick={() => handleNavigate(item.path)}
                  sx={{
                    borderRadius: 2,
                    mb: 1,
                    '&.Mui-selected': {
                      bgcolor: `${theme.palette.primary.main}15`,
                      color: theme.palette.primary.main,
                      '&:hover': {
                        bgcolor: `${theme.palette.primary.main}20`,
                      },
                      '& .MuiListItemIcon-root': {
                        color: theme.palette.primary.main,
                      },
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{
                      fontWeight: location.pathname === item.path ? 600 : 400,
                    }}
                  />
                </ListItemButton>
              </ListItem>
              {item.subItems && (
                <List component="div" disablePadding sx={{ pl: 4 }}>
                  {item.subItems.map((subItem, subIndex) => (
                    <ListItem key={subIndex} disablePadding>
                      <ListItemButton
                        selected={location.pathname === subItem.path}
                        onClick={() => handleNavigate(subItem.path)}
                        sx={{
                          borderRadius: 2,
                          mb: 0.5,
                          pl: 4,
                          '&.Mui-selected': {
                            bgcolor: `${theme.palette.primary.main}10`,
                            color: theme.palette.primary.main,
                          },
                        }}
                      >
                        <ListItemText
                          primary={subItem.text}
                          primaryTypographyProps={{
                            fontSize: '0.875rem',
                            fontWeight: location.pathname === subItem.path ? 500 : 400,
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              )}
            </React.Fragment>
          ))}
        </List>
      </Box>

      {/* Footer Section */}
      <Box sx={{ p: 2, borderTop: `1px solid ${theme.palette.divider}` }}>   
        <ListItemButton
          onClick={handleLogout}
          sx={{
            borderRadius: 2,
            color: theme.palette.error.main,
            '&:hover': {
              bgcolor: `${theme.palette.error.main}10`,
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText primary="Logout" />
        </ListItemButton>    
        {/* Copyright Section */}
        <Typography 
          variant="caption" 
          color="text.secondary"
          sx={{ 
            display: 'block', 
            textAlign: 'center', 
            mb: 2,
            fontSize: '0.75rem',
            opacity: 0.7
          }}
        >
          © {new Date().getFullYear()} Registration Management System. All rights reserved.
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* AppBar */}
      <AppBar position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          bgcolor: 'background.paper',
          color: 'text.primary',
          background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.background.paper, 0.95)} 100%)`,
          backdropFilter: 'blur(10px)',
          boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}, 0 1px 0 ${alpha(theme.palette.divider, 0.2)}`,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
          zIndex: theme.zIndex.drawer + 1,
          transition: 'all 0.3s ease',
        }}
      >
        <Toolbar sx={{ py: 1, minHeight: '70px !important' }}>
          {/* Mobile Menu Button */}
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ 
              mr: 2, 
              display: { md: 'none' },
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              '&:hover': {
                bgcolor: alpha(theme.palette.primary.main, 0.2),
                transform: 'rotate(90deg)',
              },
              transition: 'all 0.3s ease',
            }}
          >
            <MenuIcon />
          </IconButton>
          
          {/* Title Section */}
          <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
              <Box sx={{ 
                width: 6, 
                height: 24, 
                bgcolor: theme.palette.primary.main,
                borderRadius: '3px',
                background: `linear-gradient(180deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`
              }} />
              <Typography 
                variant="h4" 
                noWrap 
                sx={{ 
                  fontWeight: 700,
                  background: `linear-gradient(90deg, ${theme.palette.text.primary}, ${theme.palette.primary.main})`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  letterSpacing: '-0.02em',
                }}
              >
                Registration Management System
              </Typography>
            </Box>
          </Box>
        
        {/* Action Buttons */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {/*
          <IconButton 
            sx={{
              position: 'relative',
              bgcolor: alpha(theme.palette.primary.main, 0.05),
              '&:hover': {
                bgcolor: alpha(theme.palette.primary.main, 0.15),
                transform: 'translateY(-2px)',
                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`,
              },
              transition: 'all 0.3s ease',
            }}
          >
            <Badge 
              badgeContent={0} 
              color="error"
              sx={{
                '& .MuiBadge-badge': {
                  boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
                  animation: 'ping 1.5s infinite',
                  '@keyframes ping': {
                    '75%, 100%': {
                      transform: 'scale(1.2)',
                      opacity: 0,
                    }
                  }
                }
              }}
            >
              <NotificationsIcon />
            </Badge>
          </IconButton>
          */}
          
          {/* User Profile Button */}
          <Box sx={{ position: 'relative' }}>
            <IconButton
              onClick={handleMenuOpen}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                p: 1.2,
                borderRadius: 3,
                bgcolor: alpha(theme.palette.primary.main, 0.03),
                border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  borderColor: alpha(theme.palette.primary.main, 0.3),
                  boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
                  transform: 'translateY(-1px)',
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              <Avatar
                sx={{
                  width: 38,
                  height: 38,
                  bgcolor: theme.palette.primary.main,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.3)}`,
                  border: `2px solid ${theme.palette.background.paper}`,
                }}
              >
                {user?.users_name ? user.users_name.trim().split(' ').pop()?.charAt(0).toUpperCase() : 'U'}
              </Avatar>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <Typography variant="caption" sx={{ fontWeight: 600, lineHeight: 1 }}>
                  {user?.users_name ? user.users_name.trim().split(' ').pop() : 'User'}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                  {user?.designation ? user.designation : 'Designation'}
                </Typography>
              </Box>
              <KeyboardArrowDownIcon 
                sx={{ 
                  color: theme.palette.text.secondary,
                  transition: 'transform 0.3s ease',
                  transform: Boolean(anchorEl) ? 'rotate(180deg)' : 'rotate(0deg)'
                }} 
              />
            </IconButton>
          </Box>
        </Box>
      </Toolbar>
      </AppBar>

      {/* Enhanced User Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
        elevation: 8,
        sx: {
          mt: 1.5,
          minWidth: 200,
          borderRadius: 3,
          overflow: 'visible',
          filter: 'drop-shadow(0 10px 30px rgba(0,0,0,0.1))',
          '&:before': {
            content: '""',
            display: 'block',
            position: 'absolute',
            top: 0,
            right: 14,
            width: 12,
            height: 12,
            bgcolor: 'background.paper',
            transform: 'translateY(-50%) rotate(45deg)',
            zIndex: 0,
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
            borderLeft: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
            }
          }
        }}
      >
      {/*
      <MenuItem 
        onClick={() => { handleMenuClose(); navigate('/profile'); }}
        sx={{ 
          py: 1.5,
          borderRadius: 1,
          mx: 1,
          mt: 1,
          '&:hover': {
            bgcolor: alpha(theme.palette.primary.main, 0.08),
          }
        }}
      >
        <ListItemIcon sx={{ minWidth: 40 }}>
          <SettingsIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
        </ListItemIcon>
        <Typography variant="body2">My Profile</Typography>
        <ChevronRightIcon fontSize="small" sx={{ ml: 'auto', color: 'text.disabled' }} />
      </MenuItem>
      */}
        <MenuItem 
          onClick={() => navigate('/change-password')}
          sx={{ 
            py: 1.5,
            borderRadius: 1,
            mx: 1,
            mt: 1,
            '&:hover': {
              bgcolor: alpha(theme.palette.primary.main, 0.08),
            }
          }}
        >
          <ListItemIcon>
            <VpnKeyIcon fontSize="small" />
          </ListItemIcon>
          Change Password
        </MenuItem>

        <MenuItem 
          onClick={handleLogout}
          sx={{ 
            py: 1.5,
            borderRadius: 1,
            mx: 1,
            mb: 1,
            '&:hover': {
              bgcolor: alpha(theme.palette.error.main, 0.08),
              '& .MuiListItemIcon-root': {
                color: theme.palette.error.main,
              }
            }
          }}
        >
          <ListItemIcon sx={{ minWidth: 40 }}>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="body2">Logout</Typography>
        </MenuItem>
      </Menu>

      {/* Drawer */}
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              borderRight: `1px solid ${theme.palette.divider}`,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content Area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          mt: { xs: 8, md: 9 },
          minHeight: '100vh',
          bgcolor: 'background.default',
        }}
      >
        <Outlet />
      </Box>
    </Box>
    
  );
};

export default DashboardLayout;